import React, { useState, useMemo } from 'react';
import { FileText, Plus, Trash2, Home, Upload, Eye, Pencil, X, Save, File as FileIcon, Search, LayoutGrid, List, Tag } from 'lucide-react';
import { api } from '../services/api';
import { useToast } from './Toast';
import FilePreviewModal from './FilePreviewModal';

const PlansView = ({ files = [], setFiles, roomSpecs = [], setRoomSpecs, onUpload, projectId }) => {
    const { showToast } = useToast();
    
    // VIEW STATE
    const [viewMode, setViewMode] = useState('rooms'); // 'rooms', 'global', 'index'
    const [previewFile, setPreviewFile] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // ROOM FORM STATE
    const [newRoom, setNewRoom] = useState({ room_name: '', paint: '', flooring: '', fixtures: '', notes: '' });
    const [editingRoomId, setEditingRoomId] = useState(null);
    const [editForm, setEditForm] = useState({});

    // TAG STATE
    const [taggingFileId, setTaggingFileId] = useState(null);
    const [newTag, setNewTag] = useState('');

    // --- DERIVED DATA ---
    const globalPlans = useMemo(() => files.filter(f => f.category === 'Plan'), [files]);
    
    const allProjectFiles = useMemo(() => {
        let all = globalPlans.map(f => ({ ...f, location: 'Global Plans' }));
        roomSpecs.forEach(room => {
            if (room.files && room.files.length > 0) {
                const roomFiles = room.files.map(f => ({ ...f, location: room.room_name }));
                all = [...all, ...roomFiles];
            }
        });
        if (searchTerm) {
            all = all.filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()) || f.location.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        return all.sort((a,b) => new Date(b.uploadDate) - new Date(a.uploadDate));
    }, [files, roomSpecs, globalPlans, searchTerm]);

    // --- HANDLERS ---
    const handleGlobalUpload = async (e) => {
        const file = e.target.files[0];
        if(!file) return;
        if(!projectId) return showToast('Save Project First', 'error');
        try {
            const res = await api.uploadFileAtomic(file, projectId, null, 'Plan');
            setFiles([...files, res]);
            showToast('Plan Uploaded');
        } catch(e) { showToast('Upload failed', 'error'); }
    };

    const handleDeleteGlobal = async (id) => {
        if(window.confirm('Delete this plan?')) {
            try {
                await api.deleteProjectFile(id);
                setFiles(files.filter(f => f.id !== id));
                showToast('Deleted');
            } catch(e) { showToast('Delete failed', 'error'); }
        }
    };

    // TAGGING HANDLERS (Simulated via Local State update + API call if endpoint exists, 
    // but since we don't have a specific 'updateFile' route in this snippet, 
    // we will assume we can't save tags permanently without a backend update.
    // HOWEVER: To make this work now, we will add a 'updateFile' route to the server 
    // or just rely on the fact that we might have missed it. 
    // For now, I will implement the UI. Real persistence requires backend support for file updates.)
    
    // NOTE: To fix persistence properly, I'd need to add PUT /api/files/:id to server.js.
    // Assuming for this interaction we just want the UI or the user acknowledges the limitation.
    // Actually, I'll add a 'soft' update that might not persist if server doesn't support it,
    // but typically files table has tags column.
    
    // Since I can't touch server.js in this specific file block, I will implement the UI logic.
    // If you need persistence, we need to touch server.js again.
    
    const handleAddTag = async (fileId) => {
        if (!newTag.trim()) return;
        const file = files.find(f => f.id === fileId);
        if (!file) return;

        const currentTags = file.tags || [];
        const updatedTags = [...currentTags, newTag.trim()];
        
        // Optimistic Update
        setFiles(files.map(f => f.id === fileId ? { ...f, tags: updatedTags } : f));
        
        // TODO: Backend Save (This needs a route like PUT /api/files/:id/tags)
        // For now, it will reset on reload unless backend supports it.
        // I will add a console log to indicate this.
        console.log("Tag added (UI Only until backend update):", newTag);
        
        setNewTag('');
        setTaggingFileId(null);
    };

    const handleRemoveTag = (fileId, tagToRemove) => {
        const file = files.find(f => f.id === fileId);
        if (!file) return;
        const updatedTags = (file.tags || []).filter(t => t !== tagToRemove);
        setFiles(files.map(f => f.id === fileId ? { ...f, tags: updatedTags } : f));
    };

    // --- ROOM HANDLERS ---
    const handleAddRoom = async () => {
        if(!newRoom.room_name) return;
        if(!projectId) return showToast('Save Project First', 'error');
        try {
            const res = await api.addRoom(projectId, newRoom);
            setRoomSpecs([...roomSpecs, { ...newRoom, id: res.id, files: [] }]);
            setNewRoom({ room_name: '', paint: '', flooring: '', fixtures: '', notes: '' });
            showToast('Room Added');
        } catch(e) { showToast('Error adding room', 'error'); }
    };

    const handleDeleteRoom = async (id) => {
        if(window.confirm('Delete room?')) {
            await api.deleteRoom(id);
            setRoomSpecs(roomSpecs.filter(r => r.id !== id));
        }
    };

    const startEditing = (room) => { setEditingRoomId(room.id); setEditForm({ ...room }); };
    const saveRoomEdit = async () => {
        try {
            await api.updateRoom(editingRoomId, editForm);
            setRoomSpecs(roomSpecs.map(r => r.id === editingRoomId ? editForm : r));
            setEditingRoomId(null);
            showToast('Room Updated');
        } catch(e) { showToast('Update failed', 'error'); }
    };

    const handleRoomFileUpload = async (e, roomId) => {
        const file = e.target.files[0];
        if(!file) return;
        if(!projectId) return showToast('Save Project First', 'error');
        try {
            const res = await api.uploadFileAtomic(file, projectId, null, 'Room Spec');
            const updatedRooms = roomSpecs.map(r => {
                if(r.id === roomId) { return { ...r, files: [...(r.files||[]), res] }; }
                return r;
            });
            setRoomSpecs(updatedRooms);
            const room = updatedRooms.find(r => r.id === roomId);
            await api.updateRoom(roomId, room);
            showToast('File Attached');
        } catch(e) { showToast('Upload failed', 'error'); }
    };

    const getUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        const cleanPath = path.replace(/^\/+/, ''); 
        return `/api/files/${cleanPath.split('/').pop()}?inline=true`;
    };

    return (
        <div className="space-y-6 pb-20">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-2">
                <div className="flex gap-6">
                    <button onClick={() => setViewMode('rooms')} className={`font-bold pb-2 px-1 transition-colors flex items-center gap-2 ${viewMode==='rooms'?'text-blue-600 border-b-2 border-blue-600':'text-gray-500 hover:text-gray-700'}`}><LayoutGrid size={18}/> Room Specs</button>
                    <button onClick={() => setViewMode('global')} className={`font-bold pb-2 px-1 transition-colors flex items-center gap-2 ${viewMode==='global'?'text-blue-600 border-b-2 border-blue-600':'text-gray-500 hover:text-gray-700'}`}><FileIcon size={18}/> Global Plans</button>
                    <button onClick={() => setViewMode('index')} className={`font-bold pb-2 px-1 transition-colors flex items-center gap-2 ${viewMode==='index'?'text-blue-600 border-b-2 border-blue-600':'text-gray-500 hover:text-gray-700'}`}><List size={18}/> Index</button>
                </div>
                {viewMode === 'index' && (
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-2.5 text-gray-400"/>
                        <input className="pl-9 pr-4 py-2 bg-gray-100 rounded-full text-sm outline-none focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                )}
            </div>

            {/* --- VIEW: ROOM SPECS --- */}
            {viewMode === 'rooms' && (
                <>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-blue-100">
                        <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2"><Home size={20}/> Add Room Spec</h3>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                            <input className="border rounded p-2 text-sm" placeholder="Name" value={newRoom.room_name} onChange={e=>setNewRoom({...newRoom, room_name: e.target.value})} />
                            <input className="border rounded p-2 text-sm" placeholder="Paint" value={newRoom.paint} onChange={e=>setNewRoom({...newRoom, paint: e.target.value})} />
                            <input className="border rounded p-2 text-sm" placeholder="Flooring" value={newRoom.flooring} onChange={e=>setNewRoom({...newRoom, flooring: e.target.value})} />
                            <input className="border rounded p-2 text-sm" placeholder="Fixtures" value={newRoom.fixtures} onChange={e=>setNewRoom({...newRoom, fixtures: e.target.value})} />
                            <button onClick={handleAddRoom} className="bg-blue-600 text-white rounded font-bold hover:bg-blue-700 text-sm">Add</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {roomSpecs.map(room => (
                            <div key={room.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="flex justify-between items-start mb-3 border-b pb-2">
                                    <h4 className="font-bold text-lg text-gray-800">{room.room_name}</h4>
                                    <div className="flex gap-2">
                                        <button onClick={() => startEditing(room)} className="text-gray-400 hover:text-blue-600"><Pencil size={16}/></button>
                                        <button onClick={() => handleDeleteRoom(room.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={16}/></button>
                                    </div>
                                </div>
                                <div className="text-sm space-y-2 text-gray-700">
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="bg-gray-50 p-2 rounded border"><span className="block text-[10px] uppercase font-bold text-gray-400">Paint</span><span className="font-medium">{room.paint || '-'}</span></div>
                                        <div className="bg-gray-50 p-2 rounded border"><span className="block text-[10px] uppercase font-bold text-gray-400">Floor</span><span className="font-medium">{room.flooring || '-'}</span></div>
                                        <div className="bg-gray-50 p-2 rounded border"><span className="block text-[10px] uppercase font-bold text-gray-400">Fix</span><span className="font-medium">{room.fixtures || '-'}</span></div>
                                    </div>
                                    {room.notes && <div className="bg-yellow-50 p-2 rounded border border-yellow-100 text-xs italic text-yellow-800 mt-2">📝 {room.notes}</div>}
                                </div>
                                <div className="mt-4 pt-3 border-t">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold text-gray-500 uppercase">Attachments</span>
                                        <label className="cursor-pointer text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded text-xs font-bold border border-blue-100 flex items-center gap-1"><Plus size={12}/> Add<input type="file" className="hidden" onChange={(e) => handleRoomFileUpload(e, room.id)}/></label>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {(room.files || []).map((f, i) => (<div key={i} onClick={() => setPreviewFile(f)} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs border cursor-pointer hover:bg-white hover:border-blue-300 hover:text-blue-600"><Eye size={10}/> {f.name}</div>))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* --- VIEW: GLOBAL PLANS --- */}
            {viewMode === 'global' && (
                <div className="space-y-6">
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer relative">
                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleGlobalUpload} />
                        <div className="flex flex-col items-center gap-2 text-gray-500"><div className="bg-white p-3 rounded-full shadow-sm"><Upload size={24} className="text-blue-600"/></div><span className="font-bold text-lg">Upload Global Plans</span><span className="text-sm">Click or drag PDF/Images here</span></div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {globalPlans.map(file => {
                            const isPdf = file.type.includes('pdf') || file.name.endsWith('.pdf');
                            return (
                                <div key={file.id} className="group relative bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col h-full">
                                    {/* Thumbnail Area */}
                                    <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center cursor-pointer relative border-b overflow-hidden" onClick={() => setPreviewFile(file)}>
                                        {isPdf ? (
                                            <div className="flex flex-col items-center justify-center text-red-500 w-full h-full bg-red-50">
                                                <FileIcon size={48} className="drop-shadow-sm"/>
                                                <span className="text-xs font-bold mt-2 uppercase tracking-widest text-red-400">PDF Document</span>
                                            </div>
                                        ) : (
                                            <img src={getUrl(file.path)} alt={file.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                        )}
                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                                            <div className="bg-white/90 text-black px-3 py-1 rounded-full text-xs font-bold opacity-0 group-hover:opacity-100 shadow-sm flex items-center gap-1">
                                                <Eye size={12}/> View
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Area */}
                                    <div className="p-3 flex-1 flex flex-col">
                                        <div className="font-bold text-sm text-gray-800 mb-1 truncate" title={file.name}>{file.name}</div>
                                        <div className="text-xs text-gray-400 mb-2">{new Date(file.uploadDate).toLocaleDateString()}</div>
                                        
                                        {/* Tags */}
                                        <div className="flex flex-wrap gap-1 mt-auto">
                                            {(file.tags || []).map(tag => (
                                                <span key={tag} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded-full flex items-center gap-1">
                                                    {tag}
                                                    <button onClick={(e) => { e.stopPropagation(); handleRemoveTag(file.id, tag); }} className="hover:text-red-500"><X size={8}/></button>
                                                </span>
                                            ))}
                                            <button onClick={() => setTaggingFileId(taggingFileId === file.id ? null : file.id)} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full hover:bg-gray-200 border border-transparent hover:border-gray-300">
                                                + Tag
                                            </button>
                                        </div>

                                        {/* Tag Input */}
                                        {taggingFileId === file.id && (
                                            <div className="mt-2 flex gap-1 animate-in fade-in slide-in-from-top-1">
                                                <input autoFocus className="flex-1 border rounded px-1 py-0.5 text-xs outline-none focus:border-blue-500" placeholder="New tag..." value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddTag(file.id)} />
                                                <button onClick={() => handleAddTag(file.id)} className="bg-blue-600 text-white px-2 rounded text-xs">Add</button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Delete Action */}
                                    <button onClick={() => handleDeleteGlobal(file.id)} className="absolute top-2 right-2 bg-white text-gray-400 p-1.5 rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500 hover:bg-red-50">
                                        <Trash2 size={14}/>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    {globalPlans.length === 0 && <div className="text-center text-gray-400 py-10">No global plans uploaded yet.</div>}
                </div>
            )}

            {/* --- VIEW: INDEX --- */}
            {viewMode === 'index' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-xs">
                            <tr><th className="p-4">Name</th><th className="p-4">Location</th><th className="p-4">Type</th><th className="p-4">Date</th><th className="p-4 text-right">Action</th></tr>
                        </thead>
                        <tbody className="divide-y">
                            {allProjectFiles.map(file => (
                                <tr key={`${file.location}-${file.id}`} className="hover:bg-blue-50/50 transition-colors">
                                    <td className="p-4 font-bold text-gray-800 cursor-pointer hover:text-blue-600" onClick={() => setPreviewFile(file)}>{file.name}</td>
                                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold ${file.location === 'Global Plans' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{file.location}</span></td>
                                    <td className="p-4 text-gray-500">{file.type.split('/')[1]?.toUpperCase()}</td>
                                    <td className="p-4 text-gray-500">{new Date(file.uploadDate).toLocaleDateString()}</td>
                                    <td className="p-4 text-right"><button onClick={() => setPreviewFile(file)} className="text-blue-600 hover:text-blue-800 font-bold flex items-center justify-end gap-1"><Eye size={16}/> View</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingRoomId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center"><h3 className="font-bold text-lg">Edit Room</h3><button onClick={() => setEditingRoomId(null)}><X size={20} className="text-gray-400"/></button></div>
                        <div className="p-6 space-y-4">
                            <div><label className="text-xs font-bold text-gray-500 uppercase">Name</label><input className="w-full border rounded p-2" value={editForm.room_name} onChange={e => setEditForm({...editForm, room_name: e.target.value})} /></div>
                            <div className="grid grid-cols-2 gap-4"><div><label className="text-xs font-bold text-gray-500 uppercase">Paint</label><input className="w-full border rounded p-2" value={editForm.paint} onChange={e => setEditForm({...editForm, paint: e.target.value})} /></div><div><label className="text-xs font-bold text-gray-500 uppercase">Floor</label><input className="w-full border rounded p-2" value={editForm.flooring} onChange={e => setEditForm({...editForm, flooring: e.target.value})} /></div></div>
                            <div><label className="text-xs font-bold text-gray-500 uppercase">Fixtures</label><input className="w-full border rounded p-2" value={editForm.fixtures} onChange={e => setEditForm({...editForm, fixtures: e.target.value})} /></div>
                            <div><label className="text-xs font-bold text-gray-500 uppercase">Notes</label><textarea className="w-full border rounded p-2 h-24" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} /></div>
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2"><button onClick={() => setEditingRoomId(null)} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-200 rounded">Cancel</button><button onClick={saveRoomEdit} className="px-6 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700">Save</button></div>
                    </div>
                </div>
            )}

            {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
        </div>
    );
};
export default PlansView;